import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, Check } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';

/**
 * CodedSelect — Dropdown für codierte SAP-Werte: zeigt den SCHLÜSSEL prominent,
 * die Bezeichnung fein darunter. Das Options-Panel wird per Portal an document.body
 * gerendert (fixe Position), damit kein `overflow-hidden`/Stacking einer Eltern-
 * Karte es abschneidet oder Klicks verschluckt.
 *
 * Props: value, onChange(v), options [{ v, de, en }], lang, placeholder, invalid
 */
const CodedSelect = ({ value, onChange, options = [], lang = 'de', placeholder = '', invalid = false }) => {
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    place();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // Bei Seiten-Scroll schliessen (fixe Position wuerde sonst verrutschen) —
    // ABER Scroll INNERHALB der Optionsliste nicht abfangen (sonst nicht scrollbar).
    const onScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // Bezeichnung ohne führenden Code (z. B. "ERSA – Ersatzteile" -> "Ersatzteile")
  const descOf = useCallback((o) => {
    const lbl = (lang === 'en' ? o.en : o.de) || '';
    const esc = o.v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('^' + esc + '\\s*[–-]?\\s*');
    return lbl.replace(re, '').trim() || lbl;
  }, [lang]);

  const selected = useMemo(() => options.find((o) => o.v === value) || null, [options, value]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => o.v.toLowerCase().includes(s) || (lang === 'en' ? o.en : o.de).toLowerCase().includes(s));
  }, [options, q, lang]);

  const boxCls = isDarkMode
    ? 'bg-white/5 border-white/15 text-white hover:border-white/30'
    : 'bg-white border-slate-300 text-slate-900 hover:border-slate-400';
  const panelCls = isDarkMode ? 'bg-gray-800 border-white/15' : 'bg-white border-slate-200';

  const pick = (v) => { onChange(v); setOpen(false); setQ(''); };

  const panel = open && rect ? createPortal(
    <div ref={panelRef} style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 1000 }}
      className={`rounded-xl border shadow-2xl overflow-hidden ${panelCls}`}>
      {options.length > 7 && (
        <div className={`relative border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="…"
            className={`w-full pl-8 pr-2 py-2 text-sm outline-none bg-transparent ${isDarkMode ? 'text-white placeholder-white/30' : 'text-slate-900 placeholder-slate-400'}`} />
        </div>
      )}
      <div className="max-h-60 overflow-y-auto py-1">
        {!options.some((o) => o.v === '') && (
          <button type="button" onClick={() => pick('')}
            className={`w-full text-left px-3 py-1.5 text-sm ${isDarkMode ? 'text-white/40 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-50'}`}>
            {placeholder || '–'}
          </button>
        )}
        {filtered.map((o) => {
          const active = o.v === value;
          return (
            <button key={o.v || '__empty'} type="button" onClick={() => pick(o.v)}
              className={`w-full text-left px-3 py-1.5 flex items-center justify-between gap-2 ${
                active ? (isDarkMode ? 'bg-emerald-500/15' : 'bg-emerald-50') : (isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-50')
              }`}>
              <span className="min-w-0">
                <span className={`block text-sm font-semibold leading-tight truncate ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{o.v || descOf(o)}</span>
                {o.v && <span className={`block text-[11px] leading-tight truncate ${isDarkMode ? 'text-white/45' : 'text-slate-400'}`}>{descOf(o)}</span>}
              </span>
              {active && <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>—</div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative">
      <button ref={btnRef} type="button" onClick={toggle}
        className={`w-full rounded-lg border px-3 py-1.5 text-left transition-colors outline-none flex items-center justify-between gap-2 ${boxCls} ${invalid ? '!border-red-500 ring-1 ring-red-500/40' : ''}`}>
        <span className="min-w-0">
          {selected ? (
            <>
              <span className="block text-sm font-semibold leading-tight truncate">{selected.v || descOf(selected)}</span>
              {selected.v && <span className={`block text-[11px] leading-tight truncate ${isDarkMode ? 'text-white/45' : 'text-slate-400'}`}>{descOf(selected)}</span>}
            </>
          ) : (
            <span className={`text-sm ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>{placeholder || '–'}</span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'} ${open ? 'rotate-180' : ''} transition-transform`} />
      </button>
      {panel}
    </div>
  );
};

export default CodedSelect;
